import { useCallback } from 'react';
import { TableWithHeader } from './shared/TableWithHeader';
import { DataTableRow } from '../../common/table/TableTypes';
import { humanizeDate } from '../../helpers/humanizeDate';
import {
  DependenciesContentViewType,
  SelectedDependencyWithClearHistoryProps,
} from '../dependencies/types';
import { InactiveTag } from '../building-blocks/activity-state/InactiveTag';
import { TableTypography } from '../../common/table/utils';
import { TableItemHighlightedText } from '../dependencies/search/highlight/TableItemHighlightedText';
import { PossibleSectionInDependencies } from '../dependencies/search/types';
import { SalesforceItem } from '../../parser/ParserTypes';
import { isItemActive, translateTypeToHumanName } from '../helpers';
import { FLOW } from '@server/parser';

interface FlowsTableProps {
  items: SalesforceItem[];
  onFlowClick: (props: SelectedDependencyWithClearHistoryProps) => void;
  activeItemId?: string;
  clearHistory?: boolean;
  section?: PossibleSectionInDependencies;
  dataTableType?: string;
}

const flowsColumns = [
  { field: 'label', headerName: 'Name', width: '135px', minWidth: '135px' },
  { field: 'type', headerName: 'Type', width: '95px', minWidth: '95px' },
  { field: 'lastModified', headerName: 'Last Modified', width: '95px', minWidth: '95px' },
];

const renderRows = ({
  items,
  activeItemId,
  section = 'dependencies',
  dataTableType = FLOW,
}: Omit<FlowsTableProps, 'onFlowClick' | 'tableId'>) => {
  return items.map((row, index) => ({
    id: row.id,
    isRowActive: activeItemId === row.id,
    label: (
      <>
        <TableTypography>
          <TableItemHighlightedText
            text={row.label}
            field="label"
            section={section}
            tableType={dataTableType}
            rowIndex={index}
          />
        </TableTypography>
        <InactiveTag isActive={isItemActive(row)} />
      </>
    ),
    type: (
      <TableTypography>
        <TableItemHighlightedText
          text={translateTypeToHumanName(row.parentType)}
          field="parentType"
          section={section}
          tableType={dataTableType}
          rowIndex={index}
        />
      </TableTypography>
    ),
    lastModified: (
      <TableTypography>
        <TableItemHighlightedText
          text={humanizeDate({ dateOrTimestamp: row.updatedAt ?? '' })}
          field="lastModified"
          section={section}
          tableType={dataTableType}
          rowIndex={index}
        />
      </TableTypography>
    ),
    isClickable: true,
  }));
};

export const FlowsTable = ({
  items,
  onFlowClick,
  activeItemId,
  clearHistory,
  section = 'dependencies',
  dataTableType = FLOW,
  ...props
}: FlowsTableProps) => {
  const onRowClick = useCallback(
    (e: React.MouseEvent<HTMLTableCellElement, MouseEvent>, row: DataTableRow) => {
      const item = items.find((_item) => _item.id === row.id);
      if (item) {
        onFlowClick({
          id: item.id,
          name: item.name,
          parentType: FLOW,
          clearHistory,
          contentType: DependenciesContentViewType.default,
        });
      }
    },
    [onFlowClick, items, clearHistory],
  );

  return (
    <TableWithHeader
      tableHeader="Flows"
      key="tableHeader_flows"
      columns={flowsColumns}
      rows={renderRows({ items, activeItemId, section, dataTableType })}
      onRowClick={onRowClick}
      {...props}
      dataTableType={dataTableType}
      section={section}
    />
  );
};
